import { CrosshairIcon, LocateFixedIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { FlowChartIcon } from '@/components/atoms/Icons/FlowChartIcon';
import { Textarea } from '@/components/ui/textarea';
import useBrains from '@/hooks/useBrains';
import { selectBrainId } from '@/redux/session/selectors';
import { Dialog } from '@/shared/types/dialog';
import { Entity } from '@/shared/types/entity';
import { Intent } from '@/shared/types/intent';

import { defaultStartingText } from './util';

import styles from './ModalAutoBrain.module.scss';

interface Props {
  data: { intents: Intent[]; entities: Entity[]; dialog: Dialog };
  prompt: string;
}

const AutoBrainResults = ({ data, prompt }: Props) => {
  const { intents, entities, dialog } = data ?? {};
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const { brain } = useBrains(brainId);
  const intentsNames = intents?.map(({ intent }) => intent)?.join(', ');
  const entitiesNames = entities?.map(({ entity }) => entity)?.join(', ');

  return (
    <div className={styles.resultsContainer}>
      <Textarea autoSize disabled value={`${defaultStartingText(brain?.language)}${prompt}`} />

      <span className="text-base leading-6 font-normal">{t('auto_brain.we_generated')}: </span>
      <div className={styles.row}>
        <span className="text-sm leading-5 font-semibold">
          <FlowChartIcon className="text-muted-foreground" />
          Dialogs:
        </span>
        <span className="text-sm leading-5 font-normal">{dialog?.name}</span>
      </div>
      {intents?.length > 0 && (
        <div className={styles.row}>
          <span className="text-sm leading-5 font-semibold">
            <LocateFixedIcon size={16} className="text-muted-foreground" />
            {intents.length > 1 ? t('common.intents') : t('common.intent')}:
          </span>
          <span className="text-sm leading-5 font-normal">{intentsNames}</span>
        </div>
      )}
      {entities?.length > 0 && (
        <div className={styles.row}>
          <span className="text-sm leading-5 font-semibold">
            <CrosshairIcon size={16} className="text-muted-foreground" />
            {entities.length > 1 ? t('common.entities') : t('common.entity')}:
          </span>
          <span className="text-sm leading-5 font-normal">{entitiesNames}</span>
        </div>
      )}
    </div>
  );
};

export default AutoBrainResults;
